import { ChangeEvent, useCallback, useRef, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  selectAssistantValue,
  selectIsFileLoading,
  selectMessageLoadingInfo,
  selectMessages,
} from "../redux/selectors";
import {
  addUserMessage,
  clearMessagesAction,
  setChatHistory,
} from "../redux/slice";
import {
  deleteAssistantThreadAction,
  postMessageWithResponsesAction,
  uploadFileToAssistantDialogAction,
} from "../redux/actions";
import { hiddenSecurityPart } from "../../../shared/utils/hiddenSecurityPart";
import { getFileExtension } from "../../Meeting/helpers/getFileExtension";
import {
  FILE_CHAT_META_INSTRUCTION,
  UPLOADED_FILE_EXTENSIONS,
} from "../../../common/Chat/constants";
import { getArtifactContentAction } from "../../Files/redux/actions";
import { getFileConfig } from "../../Files/helpers";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { generateId } from "../../../shared/utils/generateId";

export const useAssistantChat = () => {
  const dispatch = useAppDispatch();
  const value = useAppSelector(selectAssistantValue);
  const messages = useAppSelector(selectMessages);
  const messageLoadingInfo = useAppSelector(selectMessageLoadingInfo);
  const isFileLoading = useAppSelector(selectIsFileLoading);
  const { selectedId } = useStorageSelectors("assistants");
  const [message, setMessage] = useState<string>("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadAbortFnRef = useRef<(() => void) | null>(null);

  const handleChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleFileUpload = (e: any) => {
    if (!e.target.files[0]) return;
    const file = e.target.files[0];
    setFileName(file.name);

    const fileExtension = getFileExtension(file.name) ?? "";
    if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
      dispatch(
        createNotification(
          "warning",
          `Расширение загруженного файла (${fileExtension}) не поддерживается`,
        ),
      );
      setFileName(null);
      setFileContent("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    const promise = dispatch(uploadFileToAssistantDialogAction(file));
    const abortFn = () => promise.abort();
    uploadAbortFnRef.current = abortFn;

    promise
      .unwrap()
      .then((text) => {
        setFileContent(text);
      })
      .catch((error) => {
        if (error !== "Aborted") {
          console.error(error);
        }
      });
  };

  const handleClick = useCallback(
    async (hash: string, fileName: string) => {
      const fileConfig = getFileConfig(fileName);
      if (!fileConfig) return;

      setFileName(fileName);
      const fileExtension = getFileExtension(fileName) ?? "";
      if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
        dispatch(
          createNotification(
            "warning",
            `Расширение загруженного файла (${fileExtension}) не поддерживается`,
          ),
        );
        setFileName(null);
        setFileContent("");
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }

      const promise = dispatch(getArtifactContentAction({ hash, fileName }));
      const abortFn = () => promise.abort();
      uploadAbortFnRef.current = abortFn;

      promise
        .unwrap()
        .then((content) => {
          setFileContent(content);
        })
        .catch((error) => {
          if (error !== "Aborted") {
            console.error(error);
          }
        });
    },
    [fileName, fileContent],
  );

  const handleSendMessage = useCallback(() => {
    if (!value?.assistant_object) {
      return;
    }
    const threadId = value.assistant_object?.thread_id
      ? value.assistant_object?.thread_id
      : `thread_${generateId()}`;
    const finalMessage = fileName
      ? `${message} <security> ${FILE_CHAT_META_INSTRUCTION} ${fileContent}</security> \n\n<file>${fileName}</file>`
      : message;
    dispatch(addUserMessage(hiddenSecurityPart(finalMessage)));
    dispatch(
      postMessageWithResponsesAction({
        message: finalMessage,
        id: selectedId ?? "",
        threadRef: threadId,
      }),
    );
    setMessage("");
    setFileName(null);
    setFileContent("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [message, fileName, fileContent, dispatch]);

  const handleSendKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [message, fileName, fileContent, dispatch],
  );

  const handleClearChat = useCallback(() => {
    if (!value?.assistant_object || !value.assistant_object?.thread_id) {
      dispatch(clearMessagesAction());
      return;
    }
    dispatch(deleteAssistantThreadAction(value.assistant_object.thread_id));
    dispatch(setChatHistory({ id: selectedId ?? "", history: [] }));
  }, [value?.assistant_object]);

  const handleDeleteFileTag = () => {
    uploadAbortFnRef.current?.();
    setFileName(null);
    setFileContent("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return {
    messages,
    message,
    fileName,
    selectedId,
    messageLoadingInfo,
    isFileLoading,
    inputRef,
    handleClick,
    handleClearChat,
    handleDeleteFileTag,
    handleFileUpload,
    handleChangeMessage,
    handleSendKeyDown,
    handleSendMessage,
  };
};
